<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Page Hero -->
      <section class="page-hero">
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <a href="/" class="breadcrumb-link">Home</a>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Print Receipt</span>
            </nav>
            <h1 class="page-title">Print Receipt</h1>
            <p class="page-subtitle">Retrieve and print your payment receipts</p>
          </div>
        </div>
      </section>

      <!-- Main Content -->
      <section class="content-section">
        <div class="container">
          <div class="content-grid">
            <!-- Left Side - Info Panel -->
            <div class="info-panel">
              <div class="info-card">
                <div class="info-card-header">
                  <div class="info-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <polyline points="6 9 6 2 18 2 18 9"></polyline>
                      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                      <rect x="6" y="14" width="12" height="8"></rect>
                    </svg>
                  </div>
                  <h3>Receipt Lookup</h3>
                </div>
                <p class="info-description">
                  Find and print any receipt from your previous payments. You can search using your M-Pesa transaction code or receipt number.
                </p>

                <div class="info-features">
                  <div class="feature-item">
                    <div class="feature-icon green">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                    </div>
                    <div>
                      <strong>Easy Search</strong>
                      <p>Find receipts by transaction code</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon blue">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="6 9 6 2 18 2 18 9"></polyline>
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                        <rect x="6" y="14" width="12" height="8"></rect>
                      </svg>
                    </div>
                    <div>
                      <strong>Instant Print</strong>
                      <p>Print or download your receipt</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon purple">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </svg>
                    </div>
                    <div>
                      <strong>Email Receipt</strong>
                      <p>Send receipt to your email</p>
                    </div>
                  </div>
                </div>

                <!-- Search Tips -->
                <div class="search-tips">
                  <h4>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="16" x2="12" y2="12"></line>
                      <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                    Search Tips
                  </h4>
                  <ul>
                    <li>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Use M-Pesa transaction code (e.g., TLBQE0NWGK)
                    </li>
                    <li>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Or use receipt number (e.g., RC2025...)
                    </li>
                    <li>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Check your M-Pesa SMS for the code
                    </li>
                  </ul>
                </div>

                <!-- Help Contact -->
                <div class="help-contact">
                  <h4>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                      <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                    Need Help?
                  </h4>
                  <div class="contact-info">
                    <div class="contact-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                      </svg>
                      <span>{{ county?.contact || '0700-000-000' }}</span>
                    </div>
                    <div class="contact-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </svg>
                      <span>{{ county?.email || 'support@county.go.ke' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Side - Form Panel -->
            <div class="form-panel">
              <!-- Step 1: Search -->
              <div v-if="!receipt" class="form-card">
                <div class="form-card-header">
                  <div class="step-badge">
                    <span class="step-number">1</span>
                    <span class="step-text">Find Receipt</span>
                  </div>
                </div>

                <div class="form-card-body">
                  <!-- Error Alert -->
                  <div v-if="error" class="alert alert-error">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="15" y1="9" x2="9" y2="15"></line>
                      <line x1="9" y1="9" x2="15" y2="15"></line>
                    </svg>
                    <span>{{ error }}</span>
                    <button @click="error = ''" class="alert-close">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>

                  <p class="form-intro">
                    Enter your M-Pesa transaction code or receipt number to find your receipt.
                  </p>

                  <div class="search-form">
                    <div class="form-group">
                      <label class="form-label">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="11" cy="11" r="8"></circle>
                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        Transaction Code / Receipt Number
                      </label>
                      <div class="input-with-icon large">
                        <input
                            type="text"
                            v-model="searchQuery"
                            class="form-input large"
                            placeholder="e.g., TLBQE0NWGK or RC2025..."
                            @keyup.enter="searchReceipt"
                        />
                        <span class="input-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                          </svg>
                        </span>
                      </div>
                      <p class="form-hint">You can find the transaction code in your M-Pesa confirmation SMS</p>
                    </div>

                    <button
                        class="btn btn-primary btn-block btn-lg"
                        @click="searchReceipt"
                        :disabled="!searchQuery.trim() || loading"
                    >
                      <span v-if="loading" class="spinner white"></span>
                      <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                      {{ loading ? 'Searching...' : 'Find Receipt' }}
                    </button>
                  </div>

                  <!-- Recent Receipts (Optional) -->
                  <div class="recent-section" v-if="recentSearches.length > 0">
                    <h4>Recent Searches</h4>
                    <div class="recent-list">
                      <button
                          v-for="(item, index) in recentSearches"
                          :key="index"
                          class="recent-item"
                          @click="searchQuery = item; searchReceipt()"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        {{ item }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 2: Receipt Found -->
              <div v-else class="form-card">
                <div class="form-card-header">
                  <div class="step-badge success">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    <span class="step-text">Receipt Found</span>
                  </div>
                  <button class="back-btn" @click="resetSearch">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="19" y1="12" x2="5" y2="12"></line>
                      <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                    New Search
                  </button>
                </div>

                <div class="form-card-body">
                  <!-- Receipt Header Card -->
                  <div class="receipt-header-card">
                    <div class="receipt-icon">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                      </svg>
                    </div>
                    <div class="receipt-info">
                      <span class="receipt-label">Receipt Number</span>
                      <span class="receipt-number">{{ receipt.receiptNo }}</span>
                      <span class="receipt-date">{{ formatDate(receipt.dateCreated) }}</span>
                    </div>
                    <div class="receipt-status">
                      <span class="status-badge paid">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        {{ receipt.status }}
                      </span>
                    </div>
                  </div>

                  <!-- Receipt Details Grid -->
                  <div class="details-section">
                    <h4 class="section-title">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                      </svg>
                      Payment Details
                    </h4>
                    <div class="details-grid">
                      <div class="detail-item">
                        <span class="detail-label">Bill Number</span>
                        <span class="detail-value">{{ receipt.billNo }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">Transaction Code</span>
                        <span class="detail-value highlight">{{ receipt.transactionCode }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">Income Type</span>
                        <span class="detail-value">{{ receipt.incomeTypeDescription }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">Description</span>
                        <span class="detail-value">{{ receipt.feeDescription }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">Payment Method</span>
                        <span class="detail-value">
                          <span class="method-badge">{{ receipt.source }}</span>
                        </span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">Paid By</span>
                        <span class="detail-value">{{ receipt.paidBy }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Location Details -->
                  <div class="details-section">
                    <h4 class="section-title">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                      Location
                    </h4>
                    <div class="details-grid">
                      <div class="detail-item">
                        <span class="detail-label">Sub County</span>
                        <span class="detail-value">{{ receipt.subCountyName }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">Ward</span>
                        <span class="detail-value">{{ receipt.wardName }}</span>
                      </div>
                      <div class="detail-item" v-if="receipt.zone">
                        <span class="detail-label">Zone</span>
                        <span class="detail-value">{{ receipt.zone }}</span>
                      </div>
                      <div class="detail-item" v-if="receipt.department">
                        <span class="detail-label">Department</span>
                        <span class="detail-value small">{{ receipt.department }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Customer Details -->
                  <div class="details-section" v-if="receipt.names || receipt.customerPhoneNumber">
                    <h4 class="section-title">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                      Customer Information
                    </h4>
                    <div class="details-grid">
                      <div class="detail-item" v-if="receipt.names">
                        <span class="detail-label">Name</span>
                        <span class="detail-value">{{ receipt.names }}</span>
                      </div>
                      <div class="detail-item" v-if="receipt.customerPhoneNumber">
                        <span class="detail-label">Phone Number</span>
                        <span class="detail-value">{{ receipt.customerPhoneNumber }}</span>
                      </div>
                      <div class="detail-item" v-if="receipt.idNo">
                        <span class="detail-label">ID Number</span>
                        <span class="detail-value">{{ receipt.idNo }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Amount Card -->
                  <div class="amount-card">
                    <div class="amount-row">
                      <span>Amount Paid</span>
                      <strong>{{ receipt.currency }} {{ formatAmount(receipt.receiptAmount) }}</strong>
                    </div>
                    <div class="amount-row" v-if="receipt.billBalance && receipt.billBalance !== '0'">
                      <span>Bill Balance</span>
                      <strong class="balance">{{ receipt.currency }} {{ formatAmount(receipt.billBalance) }}</strong>
                    </div>
                  </div>

                  <!-- Receipt Items (if multiple) -->
                  <div class="items-section" v-if="receiptInfos && receiptInfos.length > 1">
                    <h4 class="section-title">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="8" y1="6" x2="21" y2="6"></line>
                        <line x1="8" y1="12" x2="21" y2="12"></line>
                        <line x1="8" y1="18" x2="21" y2="18"></line>
                        <line x1="3" y1="6" x2="3.01" y2="6"></line>
                        <line x1="3" y1="12" x2="3.01" y2="12"></line>
                        <line x1="3" y1="18" x2="3.01" y2="18"></line>
                      </svg>
                      Receipt Items
                    </h4>
                    <div class="items-list">
                      <div
                          v-for="(item, index) in receiptInfos"
                          :key="index"
                          class="item-row"
                      >
                        <div class="item-info">
                          <span class="item-desc">{{ item.feeDescription }}</span>
                          <span class="item-customer" v-if="item.customer">{{ item.customer }}</span>
                        </div>
                        <span class="item-amount">{{ receipt.currency }} {{ formatAmount(item.receiptAmount) }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Action Buttons -->
                  <div class="action-buttons">
                    <button class="btn btn-primary btn-lg" @click="printReceipt">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="6 9 6 2 18 2 18 9"></polyline>
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                        <rect x="6" y="14" width="12" height="8"></rect>
                      </svg>
                      Print Receipt
                    </button>
                    <button class="btn btn-secondary btn-lg" @click="downloadReceipt">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      Download PDF
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <FooterComponent />
  </div>
</template>

<script>
import {authUrl, biller, execute} from "@/api";
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default {
  name: "PrintReceipt",
  components: { HeaderComponent, FooterComponent },
  data() {
    return {
      county: null,
      searchQuery: '',
      loading: false,
      error: '',
      receipt: null,
      receiptInfos: [],
      recentSearches: []
    };
  },

  mounted() {
    this.getCounty();
    this.loadRecentSearches();

    // Check for URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const receiptNo = urlParams.get('receiptNo') || urlParams.get('transactionCode');
    if (receiptNo) {
      this.searchQuery = receiptNo;
      this.searchReceipt();
    }
  },

  methods: {
    async getCounty() {
      try {
        const data = new FormData();
        data.append("function", "getCounty");
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.county = res.data.data.county;
        }
      } catch (error) {
        console.error('Error fetching county:', error);
      }
    },

    async searchReceipt() {
      if (!this.searchQuery.trim()) return;

      this.loading = true;
      this.error = '';
      this.receipt = null;
      this.receiptInfos = [];

      try {
        const data = new FormData();
        data.append("function", "getReceipt");
        data.append("receiptNo", this.searchQuery.trim());

        const res = await execute(data, biller);

        if (res.data.success && res.data.data.receiptDetails) {
          this.receipt = res.data.data.receiptDetails;
          this.receiptInfos = res.data.data.receiptInfos || [];

          if (res.data.data.county) {
            this.county = res.data.data.county;
          }

          // Save to recent searches
          this.saveRecentSearch(this.searchQuery.trim());
        } else {
          this.error = res.data.message || 'Receipt not found. Please check the transaction code or receipt number and try again.';
        }
      } catch (error) {
        console.error('Error searching receipt:', error);
        this.error = 'An error occurred while searching. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    resetSearch() {
      this.receipt = null;
      this.receiptInfos = [];
      this.searchQuery = '';
      this.error = '';
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-KE', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatAmount(amount) {
      if (!amount) return '0';
      return new Intl.NumberFormat('en-KE').format(parseFloat(amount));
    },

    printReceipt() {
      if (!this.receipt.receiptNo) {
        alert("Receipt not available yet. Please try again.");
        return;
      }

      localStorage['params'] = JSON.stringify({
        receiptNo: this.receipt.receiptNo
      });
      const routeData = this.$router.resolve({ name: 'receipt' });
      window.open(routeData.href, '_blank');
    },

    downloadReceipt() {
      if (!this.receipt.receiptNo) {
        alert("Receipt not available yet. Please try again.");
        return;
      }

      localStorage['params'] = JSON.stringify({
        receiptNo: this.receipt.receiptNo,
        download: true
      });
      const routeData = this.$router.resolve({ name: 'receipt' });
      window.open(routeData.href, '_blank');
    },

    saveRecentSearch(query) {
      let recent = JSON.parse(localStorage.getItem('recentReceiptSearches') || '[]');
      recent = recent.filter(item => item !== query);
      recent.unshift(query);
      recent = recent.slice(0, 5);
      localStorage.setItem('recentReceiptSearches', JSON.stringify(recent));
      this.recentSearches = recent;
    },

    loadRecentSearches() {
      this.recentSearches = JSON.parse(localStorage.getItem('recentReceiptSearches') || '[]');
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
}

.main-content {
  flex: 1;
  padding-top: 72px;
}

/* Page Hero */
.page-hero {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  padding: 48px 0;
  color: white;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.breadcrumb-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 14px;
}

.breadcrumb-link {
  color: rgba(255,255,255,0.7);
  text-decoration: none;
  transition: color 0.2s;
}

.breadcrumb-link:hover { color: white; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color: white; }

.page-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 8px;
}

.page-subtitle {
  opacity: 0.9;
  font-size: 16px;
}

/* Content Section */
.content-section {
  padding: 48px 0 80px;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  gap: 32px;
  align-items: start;
}

/* Info Panel */
.info-panel {
  position: sticky;
  top: 96px;
}

.info-card {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
}

.info-card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.info-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0f4c81;
}

.info-card-header h3 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
}

.info-description {
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 24px;
}

.info-features {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.feature-item {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
}

.feature-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.feature-icon.green { background: #d1fae5; color: #059669; }
.feature-icon.blue { background: #dbeafe; color: #2563eb; }
.feature-icon.purple { background: #dbeafe; color: #0f4c81; }

.feature-item strong {
  display: block;
  font-size: 14px;
  color: #1e293b;
  margin-bottom: 2px;
}

.feature-item p {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

/* Search Tips */
.search-tips {
  padding: 20px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 12px;
  margin-bottom: 20px;
}

.search-tips h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 12px;
}

.search-tips ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.search-tips li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #78350f;
  margin-bottom: 8px;
}

.search-tips li:last-child {
  margin-bottom: 0;
}

.search-tips li svg {
  color: #059669;
}

/* Help Contact */
.help-contact {
  padding: 20px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
}

.help-contact h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1e40af;
  margin-bottom: 12px;
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #3b82f6;
}

/* Form Panel */
.form-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
  overflow: hidden;
}

.form-card-header {
  padding: 24px 32px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.step-badge {
  display: flex;
  align-items: center;
  gap: 10px;
}

.step-badge .step-number {
  width: 28px;
  height: 28px;
  background: #0f4c81;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 13px;
}

.step-badge.success {
  color: #059669;
}

.step-badge.success svg {
  color: #059669;
}

.step-badge .step-text {
  font-weight: 600;
  color: #1e293b;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #64748b;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.back-btn:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.form-card-body {
  padding: 32px;
}

.form-intro {
  color: #64748b;
  margin-bottom: 24px;
  line-height: 1.6;
}

/* Alert */
.alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  margin-bottom: 20px;
  font-size: 14px;
}

.alert-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
}

.alert-close {
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: inherit;
}

/* Form Elements */
.form-group {
  margin-bottom: 24px;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 10px;
}

.input-with-icon {
  position: relative;
}

.input-with-icon.large .form-input {
  padding: 18px 56px 18px 20px;
  font-size: 17px;
}

.form-input {
  width: 100%;
  padding: 14px 48px 14px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  transition: all 0.2s;
  background: white;
}

.form-input:focus {
  outline: none;
  border-color: #0f4c81;
  box-shadow: 0 0 0 4px rgba(15, 76, 129, 0.1);
}

.form-input::placeholder {
  color: #94a3b8;
}

.input-icon {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.form-hint {
  font-size: 13px;
  color: #64748b;
  margin-top: 8px;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 24px;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-block { width: 100%; }
.btn-lg { padding: 16px 32px; font-size: 16px; }

.btn-primary {
  background: #0f4c81;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0a3459;
}

.btn-secondary {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover {
  background: #e2e8f0;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Spinner */
.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0,0,0,0.1);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner.white {
  border-color: rgba(255,255,255,0.3);
  border-top-color: white;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Recent Searches */
.recent-section {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
}

.recent-section h4 {
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 12px;
}

.recent-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.recent-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.recent-item:hover {
  background: #dbeafe;
  border-color: #0f4c81;
  color: #0f4c81;
}

/* Receipt Header Card */
.receipt-header-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-radius: 14px;
  margin-bottom: 24px;
}

.receipt-icon {
  width: 64px;
  height: 64px;
  background: white;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0f4c81;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
}

.receipt-info {
  flex: 1;
}

.receipt-label {
  display: block;
  font-size: 12px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.receipt-number {
  display: block;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 4px 0;
}

.receipt-date {
  font-size: 13px;
  color: #64748b;
}

.receipt-status {
  text-align: right;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
}

.status-badge.paid {
  background: #d1fae5;
  color: #059669;
}

/* Details Section */
.details-section {
  margin-bottom: 24px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 16px;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.detail-item {
  padding: 14px;
  background: #f8fafc;
  border-radius: 10px;
}

.detail-label {
  display: block;
  font-size: 11px;
  color: #64748b;
  margin-bottom: 4px;
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.detail-value.highlight {
  color: #0f4c81;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.detail-value.small {
  font-size: 12px;
  font-weight: 500;
}

.method-badge {
  display: inline-block;
  padding: 4px 10px;
  background: #d1fae5;
  color: #059669;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

/* Amount Card */
.amount-card {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  border-radius: 14px;
  padding: 24px;
  margin-bottom: 24px;
}

.amount-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  color: white;
}

.amount-row span {
  font-size: 14px;
  opacity: 0.9;
}

.amount-row strong {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 24px;
  font-weight: 800;
}

.amount-row strong.balance {
  font-size: 18px;
  opacity: 0.8;
}

/* Items Section */
.items-section {
  margin-bottom: 24px;
}

.items-list {
  background: #f8fafc;
  border-radius: 12px;
  overflow: hidden;
}

.item-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #e2e8f0;
}

.item-row:last-child {
  border-bottom: none;
}

.item-info {
  flex: 1;
}

.item-desc {
  display: block;
  font-size: 14px;
  color: #1e293b;
  font-weight: 500;
}

.item-customer {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.item-amount {
  font-weight: 600;
  color: #0f4c81;
}

/* Action Buttons */
.action-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

/* Responsive */
@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .info-panel {
    position: static;
  }
}

@media (max-width: 768px) {
  .form-card-body {
    padding: 24px;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }

  .action-buttons {
    grid-template-columns: 1fr;
  }

  .receipt-header-card {
    flex-direction: column;
    text-align: center;
  }

  .receipt-status {
    text-align: center;
  }

  .page-title {
    font-size: 26px;
  }
}
</style>